import React, { useContext } from 'react'
import { TextField, Grid, Divider, Button, Select, MenuItem, useMediaQuery } from '@material-ui/core'
import { EventEmitterService, EventKey } from '../../../services/EventEmitterService'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import { awaitWithLoading } from '../../../services/utils.js'
import { useHistory } from 'react-router-dom'
import FlexView from 'react-flexview/lib'
import dayjs from 'dayjs'
import LaunchIcon from '@material-ui/icons/Launch'
import { TextButton } from '../../Core/Buttons/TextButton'
import classNames from 'classnames'
import { InsuranceStatus } from '../../../models/insurance-models/InsuranceStatus'
import insuranceApi from '../../../api/insurance.api'
import { reportGTM } from '../../Core/Utilities/GTagManger'
import storageModeApi from '../../../api/storageMode.api'
import { SkyInformationPopover } from '../../Core/Popover/SkyPopover'
import { SessionService } from '../../../services/SessionService'
import { voom_signature_in_base64 } from '../../../Constants.js'
import { SessionContextStore } from '../../../context/SessionContext.js'

interface InsuredMotorcyclesSectionProps {
  insurance: ActiveInsuranceModel;
}

export const InsuredMotorcyclesSection = ({ insurance }: InsuredMotorcyclesSectionProps) => {
  const [open, setOpen] = React.useState(false)
  const [motorcyclesValidate, setMotorcyclesValidate] = React.useState([])
  const [enableStorageMode, setEnableStorageMode] = React.useState(false)
  const [curVehicleId, setCurVehicleId] = React.useState('')
  const [dialogText, setDialogText] = React.useState('')
  const [vehiclesStorageMode, setVehiclesStorageMode] = React.useState([])
  const isMobile = !useMediaQuery(theme => theme.breakpoints.up('md'))
  const history = useHistory()

  const HasOpenOdometerRequests = insurance.insuredVehicles.filter(iv => iv.have_open_odometer_request).length !== 0

  React.useEffect(() => {
    let mapper = Object.assign({}, ...insurance.insuredVehicles.map(x => ({ [x.external_id]: x })))
    setVehiclesStorageMode(mapper)
  }, [])

  React.useEffect(() => {
    if (SessionService.isAdmin() && (insurance.policyStatus === 'Active' || insurance.policyStatus === 'Expired')) {
      const setValidate = async () => {
        var res = await insuranceApi.getOdometerValidateLink(insurance.insuranceId)
        setMotorcyclesValidate(res.data ? res.data : [])
      }
      setValidate()
    }
  }, [vehiclesStorageMode])

  const onStorageModeAction = (IsInStorageMode, vehicleId) => {
    if (IsInStorageMode) {
      setDialogText('Are you sure you wish to disable storage mode for this motorcycle?')
      setEnableStorageMode(false)
    } else {
      setDialogText('Are you sure you wish to enable storage mode for this motorcycle?')
      setEnableStorageMode(true)
    }
    setCurVehicleId(vehicleId)
    setOpen(true)
  }

  const handleDisagree = () => {
    history.push(`/policy-management`)
    setOpen(false)
  }

  const handleAgree = async () => {
    setOpen(false)

    let isInStorageMode
    let res
    if (enableStorageMode) {
      res = await awaitWithLoading(
        storageModeApi.adminUploadSignedForm({
          insurance_id: insurance.insuranceId,
          vehicle_id: curVehicleId,
        })
      )
      isInStorageMode = true
    } else {
      res = await awaitWithLoading(
        storageModeApi.releaseFromStorageMode({
          insurance_id: insurance.insuranceId,
          vehicle_id: curVehicleId,
        })
      )
      isInStorageMode = false
    }

    if (res.ok) {
      if (enableStorageMode && res.data) {
        setVehiclesStorageMode(prev => {
          prev[curVehicleId].last_storage_mode_start_time = res.data.signature_date_time
          prev[curVehicleId].last_storage_mode_pdf_url = res.data.file_url
          return prev
        })
      }
      setVehiclesStorageMode(prev => {
        prev[curVehicleId].is_in_storage_mode = isInStorageMode
        return prev
      })
      history.push(`/policy-management`)
    } else {
      EventEmitterService.dispatch(EventKey.ShowError, res)
    }
  }

  const onGreenButtonClick = async id => {
    reportGTM('', '', { event: 'Upload-odometer_policy-management_mobile', at: window.location.href })
    const getUploadOdomoterUrl = async () => {
      let activeInsurance = await insuranceApi.getActiveInsurances()
      const link = await insuranceApi.getOdometerUploadLink(activeInsurance.parsedData.insuranceId)
      return link
    }
    let res = await awaitWithLoading(getUploadOdomoterUrl())
    if (res && res.data) {
      let link = res.data.find(veh => veh.vehicle_id == id)
      window.location.href = link.upload_link
    } else {
      EventEmitterService.dispatch(EventKey.ShowError, res)
    }
  }

  return (
    <React.Fragment>
      <div style={{ paddingTop: 5 }} className="margin-top-small text-small bold">
        Insured Motorcycles
      </div>
      {Object.values(vehiclesStorageMode).map(veh => {
        return (
          <Grid key={veh.id}>
            <Grid item sm={12} md={12} style={{ padding: 0 }}>
              <div className="text-small margin-top-3 ">
                {veh.make} {veh.model}
              </div>
            </Grid>
            {veh.is_in_storage_mode && (
              <>
                <FlexView hAlignContent={'left'} className={!SessionService.isAdmin() ? `margin-bottom-small` : ''}>
                  <div className="text-tiny margin-top-8">Stored since: {dayjs(veh.last_storage_mode_start_time).format('M/D/YYYY')} </div>
                  <a href={veh.last_storage_mode_pdf_url} without rel="noopener noreferrer" target="_blank">
                    <LaunchIcon className="lightgrayblue tiny-icon margin-left-3 margin-top-3"></LaunchIcon>
                  </a>
                </FlexView>
              </>
            )}
            {SessionService.isAdmin() && (
              <FlexView hAlignContent={'left'}>
                {insurance.getStatus() == InsuranceStatus.ACTIVE && !insurance.estimatedMilesInsurance && (
                  <Button
                    className={`blue bold underline ${
                      veh.is_in_storage_mode ? 'back-to-ride-control-button' : 'mark-as-stored-control-button margin-top-6'
                    }`}
                    onClick={() => onStorageModeAction(veh.is_in_storage_mode, veh.external_id)}
                  >
                    {veh.is_in_storage_mode ? 'Back to ride' : 'Mark as stored'}{' '}
                  </Button>
                )}
                {HasOpenOdometerRequests && !insurance.estimatedMilesInsurance && (
                  <SkyInformationPopover style={{ 'margin-top': '3px' }}>
                    <div className="text-small">
                      Changing the customer’s status will be possible only after the current payment will be finalized - make sure you’ve
                      approved the last uploaded odometer photo
                    </div>
                  </SkyInformationPopover>
                )}
              </FlexView>
            )}

            {SessionService.isAdmin() && motorcyclesValidate.some(m => m.vehicle_id == veh.external_id && m.url) && (
              <FlexView hAlignContent={'left'}>
                {
                  <Button className={`bold underline validate-odometer-control-button margin-top-6`}>
                    <a
                      style={{ color: 'var(--blue)' }}
                      target="_blank"
                      href={motorcyclesValidate.filter(v => v.vehicle_id == veh.external_id)[0].url}
                    >
                      {' '}
                      Pending odometer review
                    </a>
                  </Button>
                }
              </FlexView>
            )}

            {isMobile && insurance.getStatus() == InsuranceStatus.PENDING && (
              <Grid item sm={12} md={12} style={{ padding: 0 }}>
                <FlexView className={classNames('space-evenly', { 'padding-bottom-basic': isMobile })}>
                  <TextButton
                    className="text-small bold green_button"
                    onClick={() => onGreenButtonClick(veh.external_id)}
                    text={'Upload Odometer'}
                  />
                </FlexView>
              </Grid>
            )}
          </Grid>
        )
      })}
      <FlexView column className="full-height" hAlignContent={'left'} vAlignContent={'center'}>
        <Dialog open={open} onClose={handleDisagree} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
          <DialogTitle id="alert-dialog-title">Storage mode</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">{dialogText}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDisagree} color="primary">
              No
            </Button>
            <Button onClick={handleAgree} color="primary" autoFocus>
              Yes
            </Button>
          </DialogActions>
        </Dialog>
      </FlexView>
    </React.Fragment>
  )
}
